import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { inventarioService } from '../../services/api';
import { SOCKET_URL } from '../../utils/socket';
import { useAuth } from '../../context/AuthContext';
import { useConfigNegocio } from '../../hooks/useConfigNegocio';
import { parseCsv, UNIDADES_INSUMOS_DEFAULT } from '../../constants/catalogos';
import { useToast } from '../../components/ui/Toast';
import { btnPrimario, btnSecundario, chip, segmento, segmentos } from '../../components/ui/estilos';
import { entrada } from '../agenda/campos';
import { DialogoInsumo, DialogoMovimiento, TipoMov } from './DialogosInsumo';
import DetalleInsumo from './DetalleInsumo';
import { Costos, Recetas } from './RecetasCostos';
import { cant, estadoInsumo } from './insumos';

type Vista = 'insumos' | 'recetas' | 'costos';
type Filtro = 'todos' | 'bajo' | 'sin';

export default function Inventario() {
  const { usuario } = useAuth();
  // Recetas es dominio de cocina: el almacenista no las puede editar (el servidor lo bloquea).
  const vistas: [Vista, string][] = [['insumos', 'Insumos'], ...(usuario?.rol === 'almacenista' ? [] : [['recetas', 'Recetas'] as [Vista, string]]), ['costos', 'Costos y márgenes']];
  const [params, setParams] = useSearchParams();
  const vista = (vistas.find(([k]) => k === params.get('v'))?.[0] || 'insumos') as Vista;
  const [toast, say] = useToast();

  return (
    <div style={{ margin: '-22px -24px', height: 'calc(100% + 44px)', minHeight: 480, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '12px 22px', borderBottom: '1px solid var(--line)', background: 'var(--surface)', flex: 'none', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <div role="tablist" style={{ ...segmentos, width: 'fit-content', maxWidth: '100%', overflowX: 'auto' }}>
          {vistas.map(([k, l]) => <button key={k} role="tab" aria-selected={vista === k} onClick={() => setParams(k === 'insumos' ? {} : { v: k }, { replace: true })} style={{ ...segmento(vista === k), height: 40, fontSize: 15, whiteSpace: 'nowrap' }}>{l}</button>)}
        </div>
        <div style={{ flex: 1 }} />
        {/* Movimientos salió del menú: el historial de todos los insumos se abre desde aquí. */}
        <Link to="/movimientos" style={{ ...btnSecundario, height: 40, fontSize: 15, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>Movimientos</Link>
        <Link to="/mermas" style={{ ...btnSecundario, height: 40, fontSize: 15, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>Mermas</Link>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        {vista === 'insumos' && <Insumos say={say} puedeBorrar={usuario?.rol === 'admin'} />}
        {vista === 'recetas' && <Recetas say={say} />}
        {vista === 'costos' && <Costos />}
      </div>
      {toast}
    </div>
  );
}

function Insumos({ say, puedeBorrar }: { say: (m: string) => void; puedeBorrar: boolean }) {
  const cfg = useConfigNegocio();
  const unidades = parseCsv(cfg.inventario_unidades, UNIDADES_INSUMOS_DEFAULT);
  const [insumos, setInsumos] = useState<any[]>([]);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [selId, setSelId] = useState<string | null>(null);
  const [mov, setMov] = useState<TipoMov | null>(null);
  const [ficha, setFicha] = useState<null | 'nuevo' | 'editar'>(null);
  const [version, setVersion] = useState(0); // refresca el kardex tras un movimiento

  const cargar = useCallback(() => inventarioService.getInsumos().then(r => setInsumos(r.data)).catch(() => say('No se pudo cargar el inventario.')), [say]);
  useEffect(() => {
    cargar();
    // Stock bajo por ventas o comandas abiertas: se recarga para ver el número real.
    const socket = io(SOCKET_URL, { auth: { token: localStorage.getItem('token') } });
    socket.on('stock_bajo', cargar);
    return () => { socket.disconnect(); };
  }, [cargar]);

  const bajos = insumos.filter(i => i.alerta).length;
  const q = busca.trim().toLowerCase();
  const visibles = insumos.filter(i => (!q || i.nombre.toLowerCase().includes(q))
    && (filtro === 'todos' || (filtro === 'bajo' ? i.alerta : i.controla_stock === false)));
  const sel = insumos.find(i => i.id === selId) || null;
  const filtros: [Filtro, string][] = [['todos', 'Todos'], ['bajo', `Stock bajo · ${bajos}`], ['sin', 'No se inventarían']];

  const hecho = (msg: string) => { setMov(null); say(msg); cargar(); setVersion(v => v + 1); };

  return (
    <div className="paneles" style={{ height: '100%', display: 'grid', gridTemplateColumns: 'minmax(280px,1fr) clamp(300px,34%,420px)' }}>
      <section aria-label="Insumos" style={{ minHeight: 0, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar insumo…" aria-label="Buscar insumo" style={{ ...entrada, flex: 1, minWidth: 180, width: 'auto', borderRadius: 12 }} />
          <button onClick={() => setFicha('nuevo')} style={{ ...btnPrimario, height: 48, fontSize: 15 }}>+ Nuevo insumo</button>
        </div>
        <div role="group" aria-label="Filtro" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {filtros.map(([k, l]) => (
            <button key={k} onClick={() => setFiltro(k)} aria-pressed={filtro === k} style={{ height: 40, padding: '0 14px', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: 'pointer',
              border: `1px solid ${filtro === k ? 'var(--ink)' : 'var(--line-strong)'}`, background: filtro === k ? 'var(--ink)' : 'var(--surface)', color: filtro === k ? 'var(--bg)' : 'var(--ink)' }}>{l}</button>
          ))}
        </div>
        <div role="table" aria-label="Insumos" style={{ borderRadius: 14, border: '1px solid var(--line)', background: 'var(--surface)', overflow: 'hidden' }}>
          <div role="row" style={{ ...filaInsumo, fontSize: 12, fontWeight: 700, color: 'var(--muted)', background: 'var(--well)' }}>
            <span role="columnheader">INSUMO</span><span role="columnheader" style={{ textAlign: 'right' }}>DISPONIBLE</span><span role="columnheader" style={{ textAlign: 'right' }}>MÍNIMO</span><span role="columnheader">ESTADO</span>
          </div>
          {!visibles.length && <div style={{ padding: 30, textAlign: 'center', color: 'var(--muted)' }}>No hay insumos con ese filtro.</div>}
          {visibles.map(i => {
            const e = estadoInsumo(i);
            const sinStock = i.controla_stock === false;
            return (
              <button key={i.id} role="row" onClick={() => setSelId(i.id)} aria-pressed={selId === i.id} style={{ ...filaInsumo, width: '100%', border: 'none', borderTop: '1px solid var(--line)', cursor: 'pointer', textAlign: 'left', color: 'var(--ink)',
                background: selId === i.id ? 'var(--well)' : 'transparent', boxShadow: selId === i.id ? 'inset 3px 0 0 var(--brand-texto)' : 'none' }}>
                <span role="cell" style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.nombre}</span>
                <span role="cell" style={{ textAlign: 'right', fontWeight: 700 }}>{sinStock ? '—' : `${cant(i.stock_disponible)} ${i.unidad}`}</span>
                <span role="cell" style={{ textAlign: 'right', color: 'var(--muted)' }}>{sinStock ? '—' : cant(i.stock_minimo)}</span>
                <span role="cell"><span style={{ ...chip(e.bg, e.fg), fontSize: 13 }}>{e.label}</span></span>
              </button>
            );
          })}
        </div>
      </section>

      <aside aria-label="Detalle del insumo" style={{ borderLeft: '1px solid var(--line)', background: 'var(--surface)', minHeight: 0, overflowY: 'auto' }}>
        {sel
          ? <DetalleInsumo key={sel.id} insumo={sel} version={version} puedeBorrar={puedeBorrar} say={say} onMovimiento={setMov} onEditar={() => setFicha('editar')} onCambio={cargar}
              onEliminado={() => { setSelId(null); cargar(); }} />
          : <div style={{ height: '100%', display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 16 }}>Elige un insumo para ver su detalle y kardex.</div>}
      </aside>

      {mov && sel && <DialogoMovimiento insumo={sel} tipo={mov} onCerrar={() => setMov(null)} onHecho={hecho} />}
      {ficha && <DialogoInsumo insumo={ficha === 'editar' ? sel : undefined} unidades={unidades} onCerrar={() => setFicha(null)}
        onGuardado={(id, msg) => { setFicha(null); setSelId(id); say(msg); cargar(); }} />}
    </div>
  );
}

const filaInsumo: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'minmax(110px,1fr) minmax(70px,120px) minmax(36px,80px) minmax(96px,130px)', gap: 10, alignItems: 'center', padding: '12px 16px', fontSize: 15, minHeight: 52,
};

